import { getTurnstileToken } from './turnstile';

export const SEGMENT_TIMEOUT_MS = 60_000;

export interface SegmentOk {
  uploadId: string;
  /** Base64 PNG, same size as the upload image. */
  mask: string;
  /** Base64 PNG wall mask, when walls were asked for and found. */
  wallMask?: string;
  width: number;
  height: number;
  confidence: number;
}

export interface WallsOk {
  /** Base64 PNG wall mask. */
  wallMask: string;
  width: number;
  height: number;
}

export type SegmentResult = { ok: true; data: SegmentOk } | { ok: false; uploadId: string | null; error: string };
export type WallsResult = { ok: true; data: WallsOk } | { ok: false; error: string };

/** POSTs the photo to /api/segment. Never throws. */
async function post(upload: Blob, tenant: string, spaceType: string, detect: string) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), SEGMENT_TIMEOUT_MS);
  try {
    const body = new FormData();
    body.append('image', upload, 'photo.jpg');
    body.append('tenant', tenant);
    body.append('spaceType', spaceType);
    body.append('detect', detect);
    body.append('turnstileToken', await getTurnstileToken());
    const res = await fetch('/api/segment', { method: 'POST', body, signal: ctrl.signal });
    return { ok: res.ok, status: res.status, data: await res.json().catch(() => ({})) };
  } catch (err) {
    return { ok: false, status: 0, data: { error: ctrl.signal.aborted ? 'timeout' : String(err) } };
  } finally {
    clearTimeout(timer);
  }
}

/** Detects the floor, and the walls too when `walls` (stem wall). Failures return the manual-outline path. */
export async function segment(upload: Blob, tenant: string, spaceType: string, walls = false): Promise<SegmentResult> {
  const { ok, status, data } = await post(upload, tenant, spaceType, walls ? 'floor,wall' : 'floor');
  if (ok && typeof data.mask === 'string') return { ok: true, data };
  return { ok: false, uploadId: data.uploadId ?? null, error: data.error ?? `http_${status}` };
}

/** Detects only the walls, for a photo whose floor is already known (stem wall switched on later). */
export async function segmentWalls(upload: Blob, tenant: string, spaceType: string): Promise<WallsResult> {
  const { ok, status, data } = await post(upload, tenant, spaceType, 'wall');
  if (ok && typeof data.wallMask === 'string') return { ok: true, data };
  return { ok: false, error: data.error ?? `http_${status}` };
}

export function base64ToBlob(b64: string, type: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}
